import {
  CcfAvailableIcon,
  CcfAvatar,
  CcfBadge,
  CcfBox,
  CcfButton,
  CcfConversationOutlineIcon,
  CcfOverflowIcon,
  CcfSettingsIcon,
  CcfTextField,
  CcfTypography,
  CcfUpArrowIcon,
} from '@nice-ccf/ui-controls';
import styles from './ccf-copilot-crmdashboard.module.scss';
import { Checkbox, MenuItem, Select } from '@mui/material';
import {
  ArrowDownward,
  ArrowLeft,
  ArrowRight,
  ArrowUpward,
  AttachFile,
  Call,
  FastForward,
  FastRewind,
  FilterAlt,
  HelpOutline,
  HorizontalRule,
  KeyboardArrowLeft,
  Language,
  Menu,
  MonitorHeartOutlined,
  NotificationsNone,
  Search,
  StarBorder,
  TuneRounded,
} from '@mui/icons-material';
import CcfCopilotCRMFormControl from './ccf-copilot-crmform-control/ccf-copilot-crmform-control';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useMsal } from '@azure/msal-react';

/* eslint-disable-next-line */
export interface CcfCopilotCRMDashboardProps {}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    fontSize: 12,
    fontWeight: 'bold',
    borderTop: '1px solid #e0e2e6',
    borderBottom: '1px solid #e0e2e6',
    padding: '10px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: '10px',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

/**
 * CcfCopilot - used to create data for CRM table
 * @example createData()
 */
function createData(
  number: string,
  desc: string,
  channel: string,
  state: string,
  priority: string,
  assigned: string,
  updatedDate: string
) {
  return { number, desc, channel, state, priority, assigned, updatedDate };
}

const rows = [
  createData(
    'CS0002287',
    'Lost Card',
    'Web',
    'New',
    '2-High',
    'Vira',
    '2023-03-27 07:25:03'
  ),
  createData(
    'CS0002285',
    'Make Reservation',
    'Web',
    'New',
    '4-Low',
    'John',
    '2023-03-24 06:50:08'
  ),
];

/**
 * CcfCopilot - used to display Copilot Video CRM dashboard view
 * @param props -?-CcfCopilotCRMDashboardProps
 * @example <CcfCopilotCRMDashboard />
 */
export function CcfCopilotCRMDashboard(props: CcfCopilotCRMDashboardProps) {
  const {  accounts } = useMsal();
  const loginCredentialsFromSSO = accounts[0]?.name || " ";
  const loginCredentialsArray = loginCredentialsFromSSO?.split(" ");
  const agentFirstNameInitials = loginCredentialsArray[0].charAt(0).toUpperCase();
  const agentSurNameInititals = loginCredentialsArray[1].charAt(0).toUpperCase();
  const relatedLinks = [
    'Create Case',
    'Create Special Handling Notes',
    'View linked accounts',
    'View subscriptions',
    'Reset a password',
  ];
  const buttons = ['Update', 'Set Password', 'Delete'];

  return (
    <CcfBox className={styles.crmContainer}>
      <CcfBox className={styles.titleBlock}>
        <CcfTypography className={styles.title}>CRM</CcfTypography>
        <CcfOverflowIcon />
      </CcfBox>
      <CcfBox className={styles.headerBlock}>
        <CcfBox className={styles.leftBlock}>
          <CcfTypography mx={2}>ClientMax CRM</CcfTypography>
          <CcfTypography>All</CcfTypography>
          <CcfTypography>Favorites</CcfTypography>
          <CcfOverflowIcon fillColor="#ffffff" fontSize="small" />
        </CcfBox>
        <CcfBox className={styles.rightblock}>
          <CcfBox className={styles.starContactBox}>
            <CcfTypography>Contact Liam...</CcfTypography>
            <StarBorder fontSize="small" style={{ color: '#d3b481' }} />
          </CcfBox>
          <Search className={styles.headerIcon} />
          <Language className={styles.headerIcon} />
          <CcfConversationOutlineIcon className={styles.conversationIcon} />
          <HelpOutline className={styles.headerIcon} />
          <Call className={styles.headerIcon} />
          <NotificationsNone className={styles.headerIcon} />
          <CcfBadge
            label="userAvatar"
            overlap="circular"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            badgeContent={
              <CcfAvailableIcon
                className={`${styles.badgeAvatarIcon} ${styles.fillGreen}`}
              />
            }
          >
            <CcfAvatar alt="KJ" className={styles.avatarHeader}>
              {agentFirstNameInitials + agentSurNameInititals}
            </CcfAvatar>
          </CcfBadge>
        </CcfBox>
      </CcfBox>
      <CcfBox className={styles.subHeaderBlock}>
        <CcfBox className={styles.leftBlock}>
          <CcfBox className={styles.squareArrow}>
            <KeyboardArrowLeft fontSize="small" />
          </CcfBox>
          <Menu fontSize="small" />
          <CcfBox className={styles.contactView}>
            <CcfTypography>Contact</CcfTypography>
            <CcfBox className={styles.flexRow}>
              <CcfTypography>Liam Davis</CcfTypography>
              <CcfTypography style={{ fontWeight: '400' }}>
                View: Case
              </CcfTypography>
            </CcfBox>
          </CcfBox>
        </CcfBox>
        <CcfBox className={styles.rightBlock}>
          <AttachFile
            className={styles.subHeaderIcon}
            style={{ transform: 'rotate(45deg)' }}
          />
          <MonitorHeartOutlined className={styles.subHeaderIcon} />
          <TuneRounded className={styles.subHeaderIcon} />
          <CcfOverflowIcon
            fillColor="#545cbc"
            className={styles.subHeaderIcon}
            style={{ transform: 'rotate(90deg)' }}
          />
          <CcfBox className={styles.btnContainer}>
            {buttons.map((button: string) => {
              return (
                <CcfButton className={styles.btn}>
                  <CcfTypography className={styles.btnText}>
                    {button}
                  </CcfTypography>
                </CcfButton>
              );
            })}
          </CcfBox>
          <ArrowUpward className={styles.subHeaderIcon} />
          <ArrowDownward className={styles.subHeaderIcon} />
        </CcfBox>
      </CcfBox>
      <CcfBox className={styles.contactForm}>
        <CcfBox className={styles.leftSideForm}>
          <CcfCopilotCRMFormControl
            label="First name"
            id="firstName"
            value="Liam"
            controlType="input"
          />
          <CcfCopilotCRMFormControl
            label="* Last name"
            id="lastName"
            value="Davis"
            controlType="input"
          />
          <CcfCopilotCRMFormControl
            label="* Account"
            id="account"
            value="#132192693"
            controlType="input"
            inputSearchIcon
            isInputInfo
          />
          <CcfCopilotCRMFormControl
            label="Title"
            id="title"
            value=""
            controlType="input"
            isInputInfo
          />
          <CcfCopilotCRMFormControl
            label="Language"
            id="language"
            value="English"
            controlType="select"
            menuItems={['English', 'Spanish']}
          />
          <CcfCopilotCRMFormControl
            label="VIP"
            id="vip"
            value=""
            controlType=""
          />
        </CcfBox>
        <CcfBox className={styles.rightSideForm}>
          <CcfCopilotCRMFormControl
            label="* Email"
            id="email"
            value="liam.davis@gmail.com"
            controlType="input"
            inputEmailIcon
          />
          <CcfCopilotCRMFormControl
            label="Business phone"
            id="businessPhone"
            value=""
            controlType="input"
          />
          <CcfCopilotCRMFormControl
            label="Mobile phone"
            id="mobilePhone"
            value="901XXXX170"
            controlType="input"
          />
          <CcfCopilotCRMFormControl
            label="Employee number"
            id="employeeNumber"
            value=""
            controlType="input"
          />
          <CcfCopilotCRMFormControl
            label="Time zone"
            id="timezone"
            value="US/Mountain"
            controlType="select"
            menuItems={['US/Mountain', 'US/Pacific']}
          />
          <CcfCopilotCRMFormControl
            label="Notification"
            id="timezone"
            value="Enable"
            controlType="select"
            menuItems={['Enable', 'Disable']}
          />
        </CcfBox>
      </CcfBox>
      <CcfBox className={styles.crmDetailsBlock}>
        <CcfBox className={styles.btnContainer}>
          {buttons.map((button: string) => {
            return (
              <CcfButton className={styles.btn}>
                <CcfTypography className={styles.btnText}>
                  {button}
                </CcfTypography>
              </CcfButton>
            );
          })}
        </CcfBox>
        <CcfBox className={styles.relatedLinksContainer}>
          <CcfBox className={styles.title}>
            <CcfTypography>Related Links</CcfTypography>
          </CcfBox>
          <CcfBox className={styles.linkBlock}>
            {relatedLinks.map((link: string) => {
              return <CcfTypography>{link}</CcfTypography>;
            })}
          </CcfBox>
        </CcfBox>
        <CcfBox className={styles.tabscontainer}>
          <CcfBox className={`${styles.activeTab} ${styles.tab}`}>
            Cases(2)
          </CcfBox>
          <CcfBox className={styles.tab}>Locations</CcfBox>
          <CcfBox className={styles.tab}>Social Profiles</CcfBox>
        </CcfBox>
        <CcfBox className={styles.tablecontainer}>
          <CcfBox className={styles.tableActionsBlock}>
            <CcfBox className={styles.leftBlock}>
              <Menu fontSize="small" style={{ marginLeft: '10px' }} />
              <FilterAlt
                fontSize="small"
                style={{ fill: 'none', stroke: '#000000' }}
              />
              <CcfBox className={styles.inputControls}>
                <Select
                  displayEmpty
                  disabled
                  name="status"
                  variant="outlined"
                  size="small"
                  className={styles.selectBox}
                  SelectDisplayProps={{
                    style: {
                      fontSize: '12px',
                      color: '#333333',
                      fontWeight: 'bold',
                    },
                  }}
                  value="Updated"
                  IconComponent={() => (
                    <CcfUpArrowIcon className={styles.arrowIconForDropdown} />
                  )}
                >
                  <MenuItem value="Updated">Updated</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
                <CcfTextField
                  id="searchbox"
                  disabled
                  variant="outlined"
                  size="small"
                  value="Search"
                  focused={false}
                  InputProps={{
                    className: styles.inputTxt,
                  }}
                />
              </CcfBox>
            </CcfBox>
            <CcfBox className={styles.rightBlock}>
              <CcfSettingsIcon
                className={styles.subHeaderIcon}
                fontSize="small"
              />
              <HorizontalRule
                className={styles.subHeaderIcon}
                fontSize="small"
              />
              <Select
                displayEmpty
                disabled
                name="actions"
                variant="outlined"
                size="small"
                className={styles.selectBox}
                SelectDisplayProps={{
                  style: {
                    fontSize: '12px',
                    color: '#333333',
                    fontWeight: 'bold',
                  },
                }}
                value="Actions on selected rows..."
                IconComponent={() => (
                  <CcfUpArrowIcon className={styles.arrowIconForDropdown} />
                )}
              >
                <MenuItem value="Actions on selected rows...">
                  Actions on selected rows...
                </MenuItem>
              </Select>
              <CcfBox className={styles.btnContainer}>
                <CcfButton className={`${styles.btn} ${styles.newButton}`}>
                  <CcfTypography
                    className={`${styles.btnText} ${styles.txtNew}`}
                  >
                    New
                  </CcfTypography>
                </CcfButton>
              </CcfBox>
            </CcfBox>
          </CcfBox>
          <CcfBox className={styles.contactNameBlock}>
            <CcfTypography className={styles.txt}>
              Contact - Liam Davis
            </CcfTypography>
          </CcfBox>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} aria-label="CRM table">
              <TableHead>
                <TableRow>
                  <StyledTableCell className={styles.checkboxColumn}>
                    <Checkbox disabled style={{ padding: '0px' }} />
                    <Search className={styles.searchIcon} />
                  </StyledTableCell>
                  <StyledTableCell>Number</StyledTableCell>
                  <StyledTableCell>Short description</StyledTableCell>
                  <StyledTableCell>Channel</StyledTableCell>
                  <StyledTableCell>State</StyledTableCell>
                  <StyledTableCell>Priority</StyledTableCell>
                  <StyledTableCell>Assigned to</StyledTableCell>
                  <StyledTableCell>Updated</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <StyledTableRow key={row.number}>
                    <StyledTableCell
                      component="th"
                      scope="row"
                    ></StyledTableCell>
                    <StyledTableCell
                      component="th"
                      scope="row"
                      className={styles.crmId}
                    >
                      {row.number}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.desc}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.channel}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.state}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.priority}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.assigned}
                    </StyledTableCell>
                    <StyledTableCell component="th" scope="row">
                      {row.updatedDate}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CcfBox className={styles.paginationBlock}>
            <FastRewind className={styles.arrow} />
            <ArrowLeft className={styles.arrow} />
            <CcfBox className={styles.currentPage}>
              <CcfTypography className={styles.txt}>1</CcfTypography>
            </CcfBox>
            <CcfTypography className={styles.txt}>to 2 of 2</CcfTypography>
            <ArrowRight className={styles.arrow} />
            <FastForward className={styles.arrow} />
          </CcfBox>
        </CcfBox>
      </CcfBox>
    </CcfBox>
  );
}

export default CcfCopilotCRMDashboard;
